import React, { useEffect, useState } from 'react';
import { Apple, Smartphone } from 'lucide-react';

/**
 * iter 103 — `/uplift` smart redirect.
 *
 * Reached via `https://collectiveorb.app/uplift` (the public marketing
 * URL). Behavior:
 *   - iOS UA      → instant redirect to the App Store listing
 *   - Android UA  → instant redirect to the Play Store listing
 *   - Desktop UA  → renders a small landing page with both download
 *                   buttons + the Uplift wordmark
 *
 * App Store / Play Store URLs are placeholders today — once you
 * publish via TestFlight + Closed Testing they get replaced with
 * the live store URLs.
 */
const APP_STORE_URL = 'https://apps.apple.com/app/the-uplift/id0000000000';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.collectiveorb.uplift';

const detectPlatform = () => {
  const ua = (navigator.userAgent || '').toLowerCase();
  // iOS detection (covers iPhone, iPad, iPod incl. iPadOS that
  // sometimes spoofs Mac UA — we bias toward iOS only when we have
  // explicit evidence).
  const isIOS = /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'desktop';
};

const AppStoreRedirectPage = () => {
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    if (p === 'ios') {
      window.location.replace(APP_STORE_URL);
    } else if (p === 'android') {
      window.location.replace(PLAY_STORE_URL);
    }
  }, []);

  // While we're detecting / on a mobile UA mid-redirect, render
  // nothing to avoid a flash of the desktop layout.
  if (platform !== 'desktop') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="app-store-redirecting">
        <p className="text-white/70 text-sm">Opening the app store…</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] flex items-center justify-center p-6"
      data-testid="app-store-landing"
    >
      <div className="max-w-md w-full text-center">
        <h1 className="text-4xl font-extrabold tracking-tight text-[#1c1a1c] mb-2">The Uplift</h1>
        <p className="text-base text-[#586D52] mb-10">Get the app for iOS or Android.</p>

        <div className="space-y-3">
          <a
            href={APP_STORE_URL}
            className="flex items-center justify-center gap-3 bg-black hover:bg-neutral-800 text-white rounded-2xl py-4 px-6 transition-colors shadow-lg"
            data-testid="ios-store-link"
          >
            <Apple className="w-6 h-6" />
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-widest opacity-70">Download on the</div>
              <div className="text-lg font-semibold leading-tight">App Store</div>
            </div>
          </a>
          <a
            href={PLAY_STORE_URL}
            className="flex items-center justify-center gap-3 bg-black hover:bg-neutral-800 text-white rounded-2xl py-4 px-6 transition-colors shadow-lg"
            data-testid="android-store-link"
          >
            <Smartphone className="w-6 h-6" />
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-widest opacity-70">Get it on</div>
              <div className="text-lg font-semibold leading-tight">Google Play</div>
            </div>
          </a>
        </div>

      </div>
    </div>
  );
};

export default AppStoreRedirectPage;
